import { SdkContext } from '../internal'
import type { Community } from '@/lib/CommunityContext'
import type { CommunityAppearance } from '@/lib/appearance'
import { noteRowFrom, versionedRowWrite } from '@/lib/realtime/rowVersion'
import { noteSettingsVersion, versionedSettingsWrite } from '@/lib/realtime/settingsVersion'

/** Where the community's sale, refund and chargeback notices are sent. */
export interface NotificationSettings {
  /** Null when unset: the server falls back to the business e-mail, then the owner's. */
  salesNotificationEmail: string | null
  /** F-102: the abandoned checkout recovery e-mail; off by default. */
  abandonedCartEmailEnabled?: boolean
}

export class CommunitiesResource {
  constructor(private ctx: SdkContext) {}

  /** Owner only: hosted course media against the limits. */
  async mediaUsage() {
    return this.ctx.call({ httpMethod: 'GET', path: '/communities/media-usage' })
  }

  async list() {
    return this.ctx.call({ httpMethod: 'GET', path: '/communities' })
  }

  async get(id: string) {
    return this.ctx.call({ httpMethod: 'GET', path: `/communities/{id}` }, { id })
  }

  async create(community: Record<string, unknown>) {
    return this.ctx.call({ httpMethod: 'POST', path: '/communities', hasBody: true }, { body: community })
  }

  /** Sends the settings version read (`settingsUpdatedAt`); a stale write asks before saving over it. */
  async update(id: string, community: Record<string, unknown>) {
    return versionedSettingsWrite((version) => this.ctx.call({ httpMethod: 'PUT', path: `/communities/{id}`, hasBody: true }, { id, body: { ...community, ...(version ? { settingsUpdatedAt: version } : {}) } }))
  }

  /** F-191 (owner and admin): the dark logo. Since phase 4 the mode and the menu live in the theme (`communityThemes`); sending them here is 400. */
  async updateAppearance(body: { darkLogoUrl: string | null }): Promise<{ changed: boolean; appearance: CommunityAppearance }> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/communities/appearance', hasBody: true }, { body })
  }

  async delete(id: string) {
    return this.ctx.call({ httpMethod: 'DELETE', path: `/communities/{id}` }, { id })
  }

  async getCommunityByDomain(domain: string) {
    return this.ctx.call<Community | null>({ httpMethod: 'POST', path: '/communities/domain', hasBody: true }, { body: { domain } })
  }

  /** Sends the settings version read (`settingsUpdatedAt`); a stale write asks before saving over it. */
  async updateSettings(id: string, settings: Record<string, unknown>) {
    return versionedSettingsWrite((version) => this.ctx.call<Partial<Community>>({ httpMethod: 'PATCH', path: `/communities/{id}/settings`, hasBody: true }, { id, body: { ...settings, ...(version ? { settingsUpdatedAt: version } : {}) } }))
  }

  /** Reloads the community settings (staff only; member gets 403). Scoped by X-CommunityId. */
  /** Also keeps `settingsUpdatedAt`, the version the settings writes send back. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- each settings card reads its own keys
  async getSettings(): Promise<Record<string, any>> {
    const settings = await this.ctx.call<Record<string, any>>({ httpMethod: 'GET', path: '/communities/settings' }) // eslint-disable-line @typescript-eslint/no-explicit-any
    noteSettingsVersion(settings)
    return settings
  }

  /** Scoped by X-CommunityId; owner/admin only. */
  async getNotificationSettings(): Promise<NotificationSettings> {
    return noteRowFrom('notificationSettings', await this.ctx.call<NotificationSettings>({ httpMethod: 'GET', path: '/notification-settings' }))
  }

  /** An empty string or null clears the address; a field left out keeps its value. */
  async updateNotificationSettings(settings: Partial<NotificationSettings>): Promise<NotificationSettings> {
    return versionedRowWrite('notificationSettings', (v) => this.ctx.call<NotificationSettings>(
      { httpMethod: 'PUT', path: '/notification-settings', hasBody: true },
      { body: { ...settings, ...(v ? { updatedAt: v } : {}) } }
    ))
  }
}
